import {
  ChallengePickerDialog,
  EventGroupCard,
  EventGroupEditDialog,
  EventGroupUnlockEditor,
  UngroupedEventChallenges,
} from '@/components/events';
import { EventMapEditor } from '@/components/events/map/EventMapEditor';
import { MapViewToggle } from '@/components/shared/map/MapViewToggle';
import { isStructureEditable as isEventStructureEditable } from '@/components/events/eventLifecycle';
import type { Event, EventChallenge, EventChallengeGroup } from '@/config/objects/events';
import {
  Alert,
  AlertDescription,
  Button,
  Skeleton,
  toast,
  useApiClient,
  useApp,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Lightbulb, Lock, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';

/** Name of the group auto-created when challenges are added before any group exists. */
const DEFAULT_GROUP_NAME = 'Main';
const MAP_VIEW_PARAM = 'view';

export function EventChallengesTab() {
  const { event, isLoading: eventLoading } = useOutletContext<{ event: Event; isLoading: boolean }>();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { config } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get(MAP_VIEW_PARAM) === 'map' ? 'map' : 'list';

  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [editGroup, setEditGroup] = useState<EventChallengeGroup | null>(null);
  const [unlockEditorGroup, setUnlockEditorGroup] = useState<EventChallengeGroup | null>(null);
  const [pickerGroup, setPickerGroup] = useState<EventChallengeGroup | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Structural changes (groups, ordering, unlocks) allowed until the event goes live
  const isStructureEditable = isEventStructureEditable(event?.status);

  // Navigate to challenge edit form page
  const handleEditChallenge = useCallback(
    (challenge: EventChallenge) => {
      navigate(`/events/${event?.id}/challenges/${challenge.id}/edit`);
    },
    [navigate, event?.id],
  );

  // Fetch groups
  const { data: groupsData, isLoading: groupsLoading } = useQuery<{
    success: boolean;
    data: EventChallengeGroup[];
  }>({
    queryKey: ['EventGroups', event?.id],
    queryFn: () => apiClient.get(`/events/${event?.id}/groups`),
    enabled: !!event?.id,
  });

  // Fetch all challenges (including ungrouped)
  const { data: challengesData, isLoading: challengesLoading } = useQuery<{
    success: boolean;
    data: EventChallenge[];
  }>({
    queryKey: ['EventChallenges', event?.id],
    queryFn: () => apiClient.get(`/events/${event?.id}/challenges`),
    enabled: !!event?.id,
  });

  const groups = groupsData?.data || [];
  const allChallenges = challengesData?.data || [];

  // Find ungrouped challenges (challenges without a groupId)
  const ungroupedChallenges = allChallenges.filter((c) => !c.groupId);

  // Flatten all challenges from all groups for the unlock editor
  const groupedChallenges = groups.flatMap((g) => g.challenges);

  // Stacks + artifacts feed the map view's pins
  const { data: stacksData } = useQuery<{ data: { id: string; name: string; latitude: number | null; longitude: number | null }[] }>({
    queryKey: ['EventStacks', event?.id],
    queryFn: () => apiClient.get(`/events/${event?.id}/stacks`),
    enabled: !!event?.id && view === 'map',
  });
  const { data: artifactsData } = useQuery<{ data: { id: string; name: string; latitude: number | null; longitude: number | null; detectionRadius?: number | null }[] }>({
    queryKey: ['EventArtifacts', event?.id],
    queryFn: () => apiClient.get(`/events/${event?.id}/artifacts`),
    enabled: !!event?.id && view === 'map',
  });

  /** Copies the selected library challenges into a group in one request. */
  const bulkAddMutation = useMutation({
    mutationFn: ({ libraryChallengeIds, groupId }: { libraryChallengeIds: string[]; groupId: string }) =>
      apiClient.post(`/events/${event?.id}/challenges/bulk-copy`, { libraryChallengeIds, groupId }),
    onSuccess: (_data, { libraryChallengeIds }) => {
      queryClient.invalidateQueries({ queryKey: ['EventGroups', event?.id] });
      queryClient.invalidateQueries({ queryKey: ['EventChallenges', event?.id] });
      toast.success(
        `${libraryChallengeIds.length} challenge${libraryChallengeIds.length === 1 ? '' : 's'} added`,
      );
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to add challenges');
    },
  });

  /** Returns a group id to add into, auto-creating "Main" when none exist. */
  const ensureGroup = useCallback(async (): Promise<EventChallengeGroup> => {
    const existing = pickerGroup ?? groupsData?.data?.[0];
    if (existing) return existing;
    const created = await apiClient.post<{ data: EventChallengeGroup }>(
      `/events/${event?.id}/groups`,
      { name: DEFAULT_GROUP_NAME, sortOrder: 0 },
    );
    queryClient.invalidateQueries({ queryKey: ['EventGroups', event?.id] });
    toast.success(`Created starter group “${DEFAULT_GROUP_NAME}”`);
    return created.data;
  }, [apiClient, event?.id, pickerGroup, groupsData?.data, queryClient]);

  const handleBulkAdd = useCallback(
    async (libraryChallengeIds: string[]) => {
      const group = await ensureGroup();
      await bulkAddMutation.mutateAsync({ libraryChallengeIds, groupId: group.id });
    },
    [bulkAddMutation, ensureGroup],
  );

  /** Authors a brand-new challenge directly inside this event. */
  const handleNewChallenge = useCallback(async () => {
    const group = await ensureGroup();
    navigate(`/events/${event?.id}/challenges/new?groupId=${group.id}`);
  }, [ensureGroup, navigate, event?.id]);

  const isLoading = eventLoading || groupsLoading || challengesLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-32" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Event not found</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto -m-4 sm:-m-6 p-4 sm:p-6 space-y-4">
      {/* Info banner when event is active — structure is locked but content is editable */}
      {!isStructureEditable && (
        <Alert variant="default" className="bg-amber-50 border-amber-200">
          <Lock className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This event is {event.status}. Groups and ordering are locked, but you can still edit challenge content.
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Challenges</h2>
          <span className="text-sm text-muted-foreground">
            ({allChallenges.length} total, {groups.length} group{groups.length !== 1 ? 's' : ''})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MapViewToggle
            view={view}
            onViewChange={(next) =>
              setSearchParams(
                (params) => {
                  if (next === 'map') {
                    params.set(MAP_VIEW_PARAM, 'map');
                  } else {
                    params.delete(MAP_VIEW_PARAM);
                  }
                  return params;
                },
                { replace: true },
              )
            }
          />
          {isStructureEditable && (
            <>
              <Button
                variant="outline"
                size="sm"
                icon={Plus}
                onClick={() => {
                  setPickerGroup(null);
                  setPickerOpen(true);
                }}
              >
                Add Challenges
              </Button>
              <Button variant="outline" size="sm" icon={Lightbulb} onClick={handleNewChallenge}>
                New Challenge
              </Button>
              <Button onClick={() => setShowCreateGroupDialog(true)} size="sm" icon={Layers}>
                Add Group
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Map view */}
      {view === 'map' && (
        <EventMapEditor
          eventId={event.id}
          groups={groups}
          stacks={stacksData?.data ?? []}
          artifacts={artifactsData?.data ?? []}
          googleMapsApiKey={config?.googleMapsApiKey}
          editable={event.status !== 'completed' && event.status !== 'archived'}
        />
      )}

      {view === 'map' ? null : (
        <>


      {/* Empty state - no groups */}
      {groups.length === 0 && (
        <div className="text-center py-12 px-4 border-2 border-dashed rounded-xl">
          <Layers className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {isStructureEditable ? 'Let’s build this hunt' : 'No challenge groups yet'}
          </h3>
          {isStructureEditable ? (
            <ol className="mx-auto mb-5 max-w-xs space-y-1 text-left font-mono text-xs tracking-wide text-muted-foreground">
              <li>1. Create a challenge group</li>
              <li>2. Add challenges from your library</li>
              <li>3. Add teams, then publish when ready</li>
            </ol>
          ) : (
            <p className="text-muted-foreground mb-4">
              This event has no challenge groups configured
            </p>
          )}
          {isStructureEditable && (
            <div className="flex justify-center gap-2">
              <Button
                icon={Plus}
                onClick={() => {
                  setPickerGroup(null);
                  setPickerOpen(true);
                }}
              >
                Add Challenges
              </Button>
              <Button variant="outline" onClick={() => setShowCreateGroupDialog(true)} icon={Layers}>
                Create a Group First
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Groups list */}
      {groups.length > 0 && (
        <div className="space-y-0">
          {groups.map((group, index) => (
            <EventGroupCard
              key={group.id}
              eventId={event.id}
              group={group}
              allGroups={groups}
              allChallenges={groupedChallenges}
              onEdit={() => setEditGroup(group)}
              onEditUnlock={() => setUnlockEditorGroup(group)}
              onAddChallenge={() => {
                setPickerGroup(group);
                setPickerOpen(true);
              }}
              onEditChallenge={handleEditChallenge}
              isFirst={index === 0}
              isLast={index === groups.length - 1}
              disabled={!isStructureEditable}
            />
          ))}

          {/* Add new group button at bottom */}
          {isStructureEditable && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateGroupDialog(true)}
                className="border-dashed"
                icon={Plus}
              >
                Add New Group
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Ungrouped challenges section */}
      {ungroupedChallenges.length > 0 && (
        <UngroupedEventChallenges
          eventId={event.id}
          challenges={ungroupedChallenges}
          groups={groups}
          onEditChallenge={handleEditChallenge}
          disabled={!isStructureEditable}
        />
      )}
        </>
      )}

      {/* Create/Edit group dialog */}
      <EventGroupEditDialog
        eventId={event.id}
        group={editGroup}
        open={showCreateGroupDialog || !!editGroup}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateGroupDialog(false);
            setEditGroup(null);
          }
        }}
        disabled={!isStructureEditable}
      />

      {/* Unlock condition editor */}
      {unlockEditorGroup && (
        <EventGroupUnlockEditor
          eventId={event.id}
          group={unlockEditorGroup}
          allGroups={groups}
          allChallenges={groupedChallenges}
          open={!!unlockEditorGroup}
          onOpenChange={(open) => !open && setUnlockEditorGroup(null)}
          disabled={!isStructureEditable}
        />
      )}

      {/* Library picker (multi-select bulk add) */}
      <ChallengePickerDialog
        open={pickerOpen}
        onOpenChange={(open) => {
          setPickerOpen(open);
          if (!open) setPickerGroup(null);
        }}
        onAdd={handleBulkAdd}
        existingSourceIds={
          new Set(
            allChallenges
              .map((challenge) => challenge.sourceId)
              .filter((sourceId): sourceId is string => Boolean(sourceId)),
          )
        }
        groupName={pickerGroup?.name}
      />
    </div>
  );
}
